/**
 * Right-pane router. Resolves the current path locator from the selection
 * and pre-binds the lander/offer CRUD callbacks for BinomPathDetail.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import BinomPathDetail from './detail/BinomPathDetail';
import type {
  BinomDraftItem,
  BinomDraftPath,
  BinomFlowDraft,
} from '../binom-flow-draft.types';
import type { BinomPathsSelection } from './binom-paths-selection';
import type { PathLocator } from '../draft-structural';

export interface BinomPathsRightPaneProps {
  selection: BinomPathsSelection;
  draft: BinomFlowDraft;
  patchDefaultPath: (idx: number, m: (p: BinomDraftPath) => void) => void;
  patchGroupPath: (
    groupIdx: number,
    pathIdx: number,
    m: (p: BinomDraftPath) => void,
  ) => void;
  onRemoveSelected: () => void;
  onAddLanders: (loc: PathLocator, items: BinomDraftItem[]) => void;
  onRemoveLander: (loc: PathLocator, idx: number) => void;
  onMoveLander: (loc: PathLocator, from: number, to: number) => void;
  onAddOffers: (loc: PathLocator, items: BinomDraftItem[]) => void;
  onRemoveOffer: (loc: PathLocator, idx: number) => void;
  onMoveOffer: (loc: PathLocator, from: number, to: number) => void;
}

function locatorFor(selection: BinomPathsSelection): PathLocator {
  if (selection.kind === 'default-path') {
    return { groupIdx: null, pathIdx: selection.index };
  }
  return { groupIdx: selection.groupIndex, pathIdx: selection.pathIndex };
}

const BinomPathsRightPane: FC<BinomPathsRightPaneProps> = ({
  selection,
  draft,
  patchDefaultPath,
  patchGroupPath,
  onRemoveSelected,
  onAddLanders,
  onRemoveLander,
  onMoveLander,
  onAddOffers,
  onRemoveOffer,
  onMoveOffer,
}) => {
  const loc = locatorFor(selection);
  const path =
    loc.groupIdx === null
      ? draft.defaultPaths[loc.pathIdx]
      : draft.pathGroups[loc.groupIdx]?.paths[loc.pathIdx];
  if (!path) {
    return (
      <p className="text-xs text-muted-foreground">No path selected.</p>
    );
  }
  const onPatch = (m: (p: BinomDraftPath) => void) =>
    loc.groupIdx === null
      ? patchDefaultPath(loc.pathIdx, m)
      : patchGroupPath(loc.groupIdx, loc.pathIdx, m);
  return (
    <BinomPathDetail
      key={`${loc.groupIdx ?? 'def'}-${path.id}`}
      path={path}
      onPatch={onPatch}
      onRemove={onRemoveSelected}
      onAddLanders={(items) => onAddLanders(loc, items)}
      onRemoveLander={(idx) => onRemoveLander(loc, idx)}
      onMoveLander={(from, to) => onMoveLander(loc, from, to)}
      onAddOffers={(items) => onAddOffers(loc, items)}
      onRemoveOffer={(idx) => onRemoveOffer(loc, idx)}
      onMoveOffer={(from, to) => onMoveOffer(loc, from, to)}
    />
  );
};

export default BinomPathsRightPane;
